/**
 * Sydney calendar-date interpretation for `submission_files.expires_at`.
 *
 * `expires_at` is a CALENDAR DATE the vendor picked, stored as end-of-day UTC
 * (`YYYY-MM-DDT23:59:59.000Z`, see packages/ui/src/lib/expiry.ts fromDateInput).
 * Comparing it as an instant is wrong twice over: `23:59:59Z` is 09:59:59 the
 * NEXT morning in Sydney, and fractional day maths against a fixed cron hour
 * rounds every reminder threshold one day late.
 *
 * These helpers compare DATES, not instants. Intl carries the AEST/AEDT rules,
 * so no timezone library is needed — the same approach as the cron slot guards.
 */

const SYDNEY_TZ = 'Australia/Sydney';

/** `YYYY-MM-DD` for `now` in Australia/Sydney. `en-CA` formats as ISO. */
export function sydneyDateOf(now: Date | number): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: SYDNEY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(now));
}

/** Sydney hour 0–23 for `now`. Intl can emit '24' for midnight; normalise it. */
export function sydneyHourOf(now: Date | number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SYDNEY_TZ,
    hour: 'numeric',
    hour12: false,
  }).formatToParts(new Date(now));
  const hour = parts.find((p) => p.type === 'hour')?.value;
  return hour === '24' ? 0 : Number(hour);
}

/**
 * The calendar date encoded in a stored `expires_at`, or null if unparseable.
 * Read in UTC to match fromDateInput/toDateInput's storage contract.
 */
function expiryDateOf(expiresAt: string): string | null {
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) return null;
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${d.getUTCFullYear()}-${month}-${day}`;
}

/** Midnight UTC of a `YYYY-MM-DD`, for whole-day arithmetic. */
function dateToUtcMs(ymd: string): number {
  const [year, month, day] = ymd.split('-').map(Number);
  return Date.UTC(year!, month! - 1, day!);
}

/**
 * True once the Sydney calendar date has moved PAST the expiry date. A document
 * dated 8/12 is valid for the whole of 8/12 in Sydney and expired at 8/13 00:00.
 * `YYYY-MM-DD` strings compare correctly lexicographically.
 */
export function isExpiredInSydney(expiresAt: string | null, now: Date | number): boolean {
  if (!expiresAt) return false;
  const expiry = expiryDateOf(expiresAt);
  if (!expiry) return false;
  return expiry < sydneyDateOf(now);
}

/**
 * Whole calendar days from today-in-Sydney to the expiry date. 1 means "expires
 * tomorrow", 0 means "expires today", negative means already past. Never a
 * fraction, so a reminder threshold of N fires exactly N days ahead.
 */
export function daysUntilExpirySydney(expiresAt: string, now: Date | number): number | null {
  const expiry = expiryDateOf(expiresAt);
  if (!expiry) return null;
  const diffMs = dateToUtcMs(expiry) - dateToUtcMs(sydneyDateOf(now));
  return Math.round(diffMs / 86_400_000);
}

/**
 * The value to compare `expires_at` against in a PostgREST query:
 * `expires_at < cutoff` selects exactly the Sydney-expired rows.
 *
 * This exists because isExpiredInSydney is a per-row predicate and cannot be
 * pushed into the database. Comparing against midnight-UTC of the current
 * Sydney date is correct for ANY stored time-of-day, not just 23:59:59Z, so it
 * is robust to legacy or hand-written rows.
 */
export function sydneyExpiryCutoffIso(now: Date | number): string {
  return `${sydneyDateOf(now)}T00:00:00.000Z`;
}
